import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import Button from '@/components/Button';
import RarityBadge from '@/components/RarityBadge';
import Collapsible from '@/components/Collapsible';
import FavoriteDialog from '@/components/FavoriteDialog';
import PSAInventoryModal from '@/components/PSAInventoryModal';
import heartIcon from '@/assets/heart.svg';
import heartFilledIcon from '@/assets/heart_filled.svg';
import rightIcon from '@/assets/chevron_right_gray.svg';
import questionIcon from '@/assets/question_grey.svg';
import Benefits from '@/components/Benefits';
import Checkbox from '@/components/Checkbox';
import { formatCurrency } from '@/utils/formatUtils';
import { getProductThumbnailUrl } from '@/utils/imageUtils';
import useAuthStore from '@/stores/authStore';
import { redirectToLogin } from '@/utils/authUtils';
import { trackEvent } from '@/utils/googleAnalytics';
import OfficialTag from '@/components/OfficialTag';
import officialIcon from '@/assets/official_icon.svg';
import OrderRewardBanner from '@/components/OrderRewardBanner';
import { useMetadata } from '@/hooks/useMetadata';

const CardBuyInfoSection = ({ 
  name, 
  localizedRawName,
  desc, 
  notes,
  rawCode,
  rarityInfo,
  set,
  price, 
  canList,
  salesVolume,
  cardType = 'SINGLE_CARD',
  boxType,
  boxCards = [],
  psaStats = [], 
  conditionStats = [], 
  bundledQuantityStats = [],
  brandInfo,
  favoritedItems = [],
  onUpdateFavorites,
  onAttemptPurchase,
  onAttemptList,
  onListingPurchase
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const accessToken = useAuthStore((state) => state.accessToken);
  const { data: metadata, isLoading: isMetadataLoading } = useMetadata();
  
  // Check if orderRewardTips exists to conditionally render the wrapper div
  const hasOrderRewardTips = !isMetadataLoading && metadata?.orderRewardTips?.icon?.url;
  
  // Sort by price (min of regular/official) and find the lowest priced available grade
  const sortedPsaStats = [...psaStats]
    .filter(stat => stat.inventory > 0)
    .sort((a, b) => {
      const aPrices = [a.lowestPriceOnSale, a.lowestPriceOnSaleOfficial].filter(v => typeof v === 'number' && v > 0);
      const bPrices = [b.lowestPriceOnSale, b.lowestPriceOnSaleOfficial].filter(v => typeof v === 'number' && v > 0);
      const aMin = aPrices.length ? Math.min(...aPrices) : Infinity;
      const bMin = bPrices.length ? Math.min(...bPrices) : Infinity;
      return aMin - bMin;
    });
  const lowestPricedGrade = sortedPsaStats[0]?.psa?.toLowerCase() || 'psa10';
  const [selectedGrade, setSelectedGrade] = useState(lowestPricedGrade);
  const [isFavoriteDialogOpen, setIsFavoriteDialogOpen] = useState(false);

  // Sort by price (min of regular/official) and find the lowest priced available box quantity
  const sortedBoxQuantities = [...bundledQuantityStats]
    .filter(stat => stat.inventory > 0)
    .sort((a, b) => {
      const aPrices = [a.lowestPriceOnSale, a.lowestPriceOnSaleOfficial].filter(v => typeof v === 'number' && v > 0);
      const bPrices = [b.lowestPriceOnSale, b.lowestPriceOnSaleOfficial].filter(v => typeof v === 'number' && v > 0);
      const aMin = aPrices.length ? Math.min(...aPrices) : Infinity;
      const bMin = bPrices.length ? Math.min(...bPrices) : Infinity;
      return aMin - bMin;
    });
  const lowestPricedBoxQuantity = sortedBoxQuantities[0]?.quantity || 1;
  const [selectedBoxQuantity, setSelectedBoxQuantity] = useState(lowestPricedBoxQuantity);
  const [psaInventoryModal, setPsaInventoryModal] = useState({
    isOpen: false,
    psa: null,
    psaLabel: '',
    inventory: 0
  });
  
  // Check if all options are sold out
  const allOptionsSoldOut = cardType === 'SINGLE_CARD'
    ? psaStats.every(stat => stat.inventory === 0)
    : bundledQuantityStats.every(stat => stat.inventory === 0);
  
  // Transform PSA stats from API to match UI format
  const gradeOptions = psaStats.map(stat => {
    const hasOfficial = stat.lowestPriceOnSaleOfficial > 0;
    const hasRegular = stat.lowestPriceOnSale > 0;
    
    let subLabel = '';
    if (stat.inventory === 0) {
      subLabel = t('common.sold_out', 'Sold Out');
    } else if (hasOfficial && (!hasRegular || stat.lowestPriceOnSaleOfficial <= stat.lowestPriceOnSale)) {
      // Official price is lower or only official price available
      subLabel = formatCurrency(stat.lowestPriceOnSaleOfficial);
    } else if (hasRegular) {
      // Only regular price or regular price is lower
      subLabel = formatCurrency(stat.lowestPriceOnSale);
    } else {
      subLabel = t('listing.waiting_for_listing');
    }
    
    return {
      value: stat.psa.toLowerCase(),
      label: stat.psa.replace('PSA_', 'PSA '),
      subLabel,
      disabled: stat.inventory == 0,
    };
  }) || [];

  // Transform PSA stats for the favorite dialog
  const favoriteSelections = cardType === 'SINGLE_CARD' 
    ? psaStats.map(stat => ({
        id: stat.psa.toLowerCase().replace('_', ''),
        label: stat.psa.replace('PSA_', 'PSA '),
        subLabel: stat.lowestPriceOnSaleOfficial > 0 && (!stat.lowestPriceOnSale || stat.lowestPriceOnSaleOfficial <= stat.lowestPriceOnSale) ? formatCurrency(stat.lowestPriceOnSaleOfficial) : stat.lowestPriceOnSale ? formatCurrency(stat.lowestPriceOnSale) : t('common.sold_out', 'Sold Out'),
        // disabled: stat.inventory === 0,
      }))
    : bundledQuantityStats.map(stat => {
        const unit = boxType === 'PACK' ? t('box.pack') : t('box.box_unit');
        return {
          id: stat.quantity.toString(),
          label: `${stat.quantity} ${unit}`,
          subLabel: stat.lowestPriceOnSaleOfficial > 0 && (!stat.lowestPriceOnSale || stat.lowestPriceOnSaleOfficial <= stat.lowestPriceOnSale) ? formatCurrency(stat.lowestPriceOnSaleOfficial) : stat.lowestPriceOnSale ? formatCurrency(stat.lowestPriceOnSale) : t('common.sold_out', 'Sold Out'),
          // disabled: stat.inventory === 0,
        };
      });

  // For SINGLE cards
  const selectedPriceInfo = gradeOptions.find(g => g.value === selectedGrade);
  
  // For BOX cards - find selected quantity info
  const selectedBoxInfo = bundledQuantityStats.find(item => item.quantity === selectedBoxQuantity);
  
  // For BOX cards - find selected quantity info
  const selectedBoxIsOfficial = selectedBoxInfo?.lowestPriceOnSaleOfficial > 0 && (!selectedBoxInfo?.lowestPriceOnSale || selectedBoxInfo?.lowestPriceOnSaleOfficial <= selectedBoxInfo?.lowestPriceOnSale);
  const selectedBoxHasBothPrices = selectedBoxInfo?.lowestPriceOnSale > 0 && selectedBoxInfo?.lowestPriceOnSaleOfficial > 0 && selectedBoxInfo?.lowestPriceOnSale <= selectedBoxInfo?.lowestPriceOnSaleOfficial;
  
  // For SINGLE cards - check if current grade has both prices
  const selectedGradeInfo = psaStats.find(stat => stat.psa.toLowerCase() === selectedGrade);
  const selectedGradeHasBothPrices = selectedGradeInfo?.lowestPriceOnSale > 0 && selectedGradeInfo?.lowestPriceOnSaleOfficial > 0 && selectedGradeInfo?.lowestPriceOnSale <= selectedGradeInfo?.lowestPriceOnSaleOfficial;
  

  // Display appropriate price based on card type
  const chosenPrice = cardType === 'SINGLE_CARD' 
    ? (selectedPriceInfo?.subLabel || formatCurrency(price || 0))
    : (selectedBoxInfo?.lowestPriceOnSale || selectedBoxInfo?.lowestPriceOnSaleOfficial
        ? (selectedBoxIsOfficial ? formatCurrency(selectedBoxInfo.lowestPriceOnSaleOfficial) : formatCurrency(selectedBoxInfo.lowestPriceOnSale))
        : formatCurrency(price || 0));
  
  const handleFavoriteConfirm = (selectedIds) => {
    onUpdateFavorites(selectedIds);
  };
  
  const handleRemoveAllFavorites = () => {
    onUpdateFavorites([]);
  };
  
  const isFavorited = favoritedItems.length > 0;

  const handleViewDetails = (condition) => {
    navigate(`/card/${id}/listing`, { state: { filter: condition } });
  };

  const handleSell = () => {
    trackEvent('listitem_click');
    onAttemptList();
  };

  const handleBuy = (isOfficial = false) => {
    trackEvent('buynow_click');

    let purchaseSelection = {};

    if (cardType === 'SINGLE_CARD') {
      purchaseSelection.selectedGrade = selectedGrade; // e.g., "psa10"
    } else { // CARD_BOX
      purchaseSelection.selectedBoxQuantity = selectedBoxQuantity; // e.g., 1
    }
    purchaseSelection.isOfficial = isOfficial; // whether the official price is selected
    onAttemptPurchase(purchaseSelection);
  };

  const handleBrandClick = () => {
    if (brandInfo?.id) {
      navigate(`/brands/${brandInfo.id}`);
    }
  };

  const handleFavoriteClick = () => {
    if (!accessToken) {
      redirectToLogin(navigate, location);
      return;
    }

    setIsFavoriteDialogOpen(true);
  };

  const handleInventoryClick = (stat) => {
    if (stat.inventory > 0) {
      setPsaInventoryModal({
        isOpen: true,
        psa: stat.psa,
        psaLabel: stat.psa.replace('PSA_', 'PSA '),
        inventory: stat.inventory
      });
    }
  };

  const handleCloseInventoryModal = () => {
    setPsaInventoryModal({
      isOpen: false,
      psa: null,
      psaLabel: '',
      inventory: 0
    });
  };

  const totalInventory = cardType === 'SINGLE_CARD' 
    ? psaStats.reduce((sum, stat) => sum + stat.inventory, 0)
    : bundledQuantityStats.reduce((sum, stat) => sum + stat.inventory, 0);

  return (
    <div className="w-full">
      {/* Title and Subtitle */}
      <div className="mb-4">
        <div className="self-stretch inline-flex flex-col justify-start items-start gap-2">
          {cardType === 'SINGLE_CARD' ? (
            <>
              <h1 className="text-2xl font-bold text-[#14151a] leading-9">
                {localizedRawName || name}
                {rarityInfo?.sub?.name && `(${rarityInfo.sub.name})`}
              </h1>
              <div className="self-stretch flex flex-col justify-start items-start gap-1">
                <div className="self-stretch flex flex-col justify-start items-start gap-1">
                  <div className="self-stretch inline-flex justify-start items-center gap-1">
                    <span className="text-[#111111] text-sm leading-[21px]">
                      {rarityInfo?.main && (
                        <RarityBadge rarity={rarityInfo.main} className="mr-1" />
                      )}

                      {(set?.code || (rawCode && cardType !== 'CARD_BOX')) && (
                        <span className="h-[18px] px-1 bg-[#f4f4f4] rounded-[3px] outline outline-[0.50px] outline-offset-[-0.50px] outline-[#dddddd] mr-1 inline-flex justify-center items-center align-middle">
                          <span className="text-[#111111] text-sm font-normal leading-none">
                            {cardType === 'CARD_BOX'
                              ? set?.code
                              : (set?.code && rawCode ? `${set.code} ${rawCode}` : set?.code || rawCode)}
                          </span>
                        </span>
                      )}

                      <span className="align-middle">{notes}</span>
                    </span>
                  </div>
                </div>
                <div className="opacity-70 justify-start text-[#aaaaaa] text-sm leading-[21px]">
                  {set?.localizedName}
                </div>
              </div>
            </>
          ) : (
            <>
              <h1 className="text-2xl font-bold text-[#14151a] leading-9">
                {localizedRawName || name}
              </h1>
              <div className="self-stretch inline-flex justify-start items-center gap-1">
                <span className="flex-1 justify-start text-[#111111] text-sm leading-[21px]">
                  {set?.code && (
                    <span className="h-[18px] px-1 bg-[#f4f4f4] rounded-[3px] outline outline-[0.50px] outline-offset-[-0.50px] outline-[#dddddd] mr-1 flex items-center float-left">
                      <span className="justify-start text-[#111111] text-sm leading-[14px]">{set.code}</span>
                    </span>
                  )}
                  {notes}
                </span>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Price and Favorite */}
      <div className="pt-px justify-between items-start flex">
        <div className="flex items-center gap-3">
          <div className="pl-3 flex items-center gap-1">
            <span className="text-lg font-medium">¥</span>
            <span className="text-[32px] font-medium leading-[48px]">{formatCurrency(price || 0).replace('¥', '')}～</span>
          </div>
          <span className="text-sm text-[#7f7f8e]">
            {salesVolume === 0 
              ? '' 
              : salesVolume > 9000 
                ? `${salesVolume}+ ${t('details.sale_orders')}` 
                : `${salesVolume} ${t('details.sale_orders')}`}
          </span>
        </div>
        <button 
          className="px-[35px] py-2.5 rounded-[26px] text-sm flex items-center gap-1"
          onClick={handleFavoriteClick}
        >
          <img 
            src={isFavorited ? heartFilledIcon : heartIcon} 
            alt="Favorite" 
            className="w-[13px] h-3" 
          />
          <span className={`text-sm ${isFavorited ? 'text-[#ff5501]' : 'text-black'}`}>
            {isFavorited ? t('favorite.added') : t('favorite.button')}
          </span>
        </button>
      </div>

      {/* PSA Grade Selection */}
      {cardType === 'SINGLE_CARD' && (
        <div className="mt-4">
          <div className="flex items-center gap-[11px] mb-3">
            <h2 className="text-sm font-medium">{t('psa.grade')}</h2>
            <button 
              onClick={() => window.open('/terms/quality_guideline_psa', '_blank')}
              className="flex items-center justify-center w-[14px] h-[14px]"
            >
              <img src={questionIcon} alt="Quality guideline" className="w-[14px] h-[14px]" />
            </button>
          </div>

          {/* Updated PSA Panel Design */}
          <div className="w-full px-6 bg-white rounded-md border border-[#dddddd] flex-col justify-start items-start overflow-hidden mb-4">
            {psaStats.map((stat, index) => {
              const gradeValue = stat.psa.toLowerCase();
              const isSelected = selectedGrade === gradeValue;
              const isDisabled = stat.inventory === 0;
              const gradeName = stat.psa.replace('PSA_', 'PSA ');
              const isOfficial = stat.lowestPriceOnSaleOfficial > 0 && (!stat.lowestPriceOnSale || stat.lowestPriceOnSaleOfficial <= stat.lowestPriceOnSale);
              const lowestPriceOnSale = isOfficial ? stat.lowestPriceOnSaleOfficial : stat.lowestPriceOnSale;
              
              return (
                <div 
                  key={index} 
                  className="w-full py-3 bg-white border-b border-[#eeeeee] last:border-b-0 flex justify-between items-center"
                >
                  <div className="flex justify-start items-center gap-4">
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => !isDisabled && setSelectedGrade(gradeValue)}
                      disabled={isDisabled}
                    />
                    
                    <div className="flex justify-start items-center gap-1">
                      <div className={`w-14 justify-center text-base font-normal font-['Roboto'] leading-normal ${
                        isDisabled ? 'text-[#aaaaaa]' : 'text-[#111111]'
                      }`}>
                        {gradeName}
                      </div>
                      
                      {stat.inventory > 0 && (
                        <button 
                          className="px-2 py-0.5 bg-[#f7f7f7] rounded-[22px] border border-[#dddddd] flex justify-center items-center gap-1.5 hover:bg-[#eeeeee] transition-colors cursor-pointer"
                          onClick={() => handleInventoryClick(stat)}
                        >
                          <div className="justify-center text-[#666666] text-sm font-normal font-['Roboto'] leading-[21px]">
                            {stat.inventory}
                          </div>
                          <img src={rightIcon} alt="chevron right" className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                    {stat.lowestPriceOnSaleOfficial > 0 && (
                          <OfficialTag />
                        )}
                  </div>
                  
                  <div className={`text-right justify-center text-base font-normal font-['Roboto'] leading-normal ${
                    isDisabled ? 'text-[#aaaaaa]' : 'text-[#111111]'
                  }`}>
                    {lowestPriceOnSale 
                      ? formatCurrency(lowestPriceOnSale)
                      : isDisabled 
                        ? t('listing.waiting_for_listing')
                        : t('common.sold_out', 'Sold Out')
                    }
                  </div>
                </div>
              );
            })}
          </div>

          {/* No PSA Grade Panel - Updated to use conditionStats */}
          <Collapsible
            title={t('psa.no_grade')}
            className="px-4"
            hint={t('box.no_psa_grade_items', {
              count: conditionStats?.reduce((sum, stat) => sum + stat.inventory, 0) || 0,
              price: formatCurrency((() => {
                const validPrices = conditionStats.filter(stat => stat.lowestPriceOnSale > 0).map(stat => stat.lowestPriceOnSale);
                return validPrices.length > 0 ? Math.min(...validPrices) : 0;
              })())
            })}
          >
            <div className="flex flex-col gap-2">
              {(conditionStats || []).map((stat, index) => {
                const isDisabled = stat.inventory === 0;
                return (
                  <div
                    key={index}
                    onClick={() => !isDisabled ? handleViewDetails(stat.condition) : null}
                    className={`flex justify-between items-center border-b border-[#f4f4f4] py-2 relative ${
                        isDisabled ? 'text-[#aaaaaa]' : 'text-[#111111] cursor-pointer'
                    }`}
                  >
                    {stat.lowestPriceOnSale ? (
                      <span>{formatCurrency(stat.lowestPriceOnSale)}~</span>
                    ) : (
                      <span>-</span>
                    )}
                    <span className="absolute left-1/2 transform -translate-x-1/2">{t(`sell.product_info.conditions.${stat.condition.toLowerCase()}`)}</span>
                    {!isDisabled ? (
                      <button
                        className="px-2 py-0.5 bg-[#f7f7f7] rounded-[22px] border border-[#dddddd] flex justify-center items-center gap-1.5 hover:bg-[#eeeeee] transition-colors cursor-pointer"
                      >
                        <div className="justify-center text-[#666666] text-sm font-normal font-['Roboto'] leading-[21px]">
                          {stat.inventory}
                        </div>
                        <img src={rightIcon} alt="chevron right" className="w-3 h-3" />
                      </button>
                    ) : (
                      <div className="text-right justify-center text-base font-normal font-['Roboto'] leading-normal text-[#aaaaaa]">
                        {t('listing.waiting_for_listing')}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
            <Button variant="default" size="l" className="w-full mt-4" onClick={() => handleViewDetails('ALL')}>
              {t('inventory.view_details')}
            </Button>
          </Collapsible>
        </div>
      )}

      {/* Box Quantity Selection */}
      {cardType === 'CARD_BOX' && (
        <div className="w-full mt-4">
          <div className="w-full h-6 pt-0.5 pb-px items-start gap-[9px] inline-flex">
            <div className="text-[#111111] text-sm font-medium">{t('box.quantity', 'Quantity')}</div>
            {totalInventory > 0 && (
              <div className="px-[8px] py-0.5 bg-[#ff2b00]/10 rounded-2xl border border-[#ff2b00]/20 text-xs text-[#ff5501]">
                {t('box.only_count_left', { count: totalInventory })}
              </div>
            )}
          </div>
          
          <div className="w-full max-h-[264px] mt-3 px-6 bg-white rounded-md border border-[#dddddd] flex-col justify-start items-start inline-flex overflow-y-auto">
            <div className="w-full flex-col justify-start items-start flex">
              {bundledQuantityStats.map((option, index) => {
                const isSelected = selectedBoxQuantity === option.quantity;
                const isDisabled = option.inventory === 0;
                const isOfficial = option.lowestPriceOnSaleOfficial > 0 && (!option.lowestPriceOnSale || option.lowestPriceOnSaleOfficial <= option.lowestPriceOnSale);
                const lowestPriceOnSale = isOfficial ? option.lowestPriceOnSaleOfficial : option.lowestPriceOnSale;
                return (
                  <div key={index} className="w-full py-3 bg-white border-b border-[#eeeeee] last:border-b-0 flex justify-between items-center">
                    <div className="flex justify-start items-center gap-4">
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => !isDisabled && setSelectedBoxQuantity(option.quantity)}
                        disabled={isDisabled}
                      />
                      <div className="flex justify-start items-center gap-1">
                        <div className={`text-base font-normal font-['Roboto'] leading-normal ${
                          isDisabled ? 'text-[#aaaaaa]' : 'text-[#111111]'
                        }`}>
                          {option.quantity} {boxType === 'PACK' ? t('box.pack') : t('box.box_unit')}
                        </div>
                        {option.lowestPriceOnSaleOfficial > 0 && (
                          <OfficialTag />
                        )}
                      </div>
                    </div>
                    
                    <div className={`text-right justify-center text-base font-normal font-['Roboto'] leading-normal ${
                      isDisabled ? 'text-[#aaaaaa]' : 'text-[#111111]'
                    }`}>
                      {lowestPriceOnSale 
                        ? formatCurrency(lowestPriceOnSale)
                        : isDisabled 
                          ? t('box.waiting_for_listing')
                          : t('box.waiting_for_listing')
                      }
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Order Reward Banner */}
      {hasOrderRewardTips && (
        <div className="mt-4">
          <OrderRewardBanner orderRewardTips={metadata.orderRewardTips} />
        </div>
      )}

      {/* Action Buttons */}
      <div className="mt-8 flex gap-6">
        {canList && (
          <Button variant="default" size="xl" className="w-[167px]" onClick={handleSell}>
            {t('listing.list_item')}
          </Button>
        )}
        {(cardType === 'CARD_BOX' && selectedBoxHasBothPrices) || (cardType === 'SINGLE_CARD' && selectedGradeHasBothPrices) ? (
        // Split button: left black (regular price) + right red (official price)
          <div className="flex-1 flex rounded-[28px] overflow-hidden">
            <button
              className="flex-1 bg-[#FF5601] text-white h-14 flex items-center justify-center gap-3 hover:bg-[#e64d00] active:bg-[#cc4400] transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-lg font-bold leading-[27px]"
              onClick={() => handleBuy(true)}
              disabled={allOptionsSoldOut}
            >
              <div className="flex items-center justify-center gap-2">
                <div className="relative">
                  <span className="text-xs">¥</span>
                  <span className="text-lg font-medium ml-[2px]">
                    {cardType === 'CARD_BOX' 
                      ? formatCurrency(selectedBoxInfo.lowestPriceOnSaleOfficial).replace('¥', '')
                      : formatCurrency(selectedGradeInfo.lowestPriceOnSaleOfficial).replace('¥', '')
                    }
                  </span>
                </div>
                <div className="flex items-center gap-[2px]">
                  <img src={officialIcon} alt="Official" className="block w-[10px] h-[10px]" />
                  <div className="font-bold text-[10px] leading-[10px] translate-y-[0.5px] text-white">
                    {t('box.official')}
                  </div>
                </div>
              </div>
            </button>
            <button
              className="flex-1 bg-[#111111] text-white h-14 flex items-center justify-center gap-3 hover:bg-[#666666] active:bg-black transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-lg font-bold leading-[27px]"
              onClick={() => handleBuy(false)}
              disabled={allOptionsSoldOut}
            >
              <div className="flex items-center justify-center">
                <div className="relative">
                  <span className="text-xs">¥</span>
                  <span className="text-lg font-medium ml-[2px]">
                    {cardType === 'CARD_BOX' 
                      ? formatCurrency(selectedBoxInfo.lowestPriceOnSale).replace('¥', '')
                      : formatCurrency(selectedGradeInfo.lowestPriceOnSale).replace('¥', '')
                    }
                  </span>
                </div>
              </div>
            </button>
          </div>
        ) : (
          // Single button
          <Button
            variant="primary"
            size="xl"
            className={`flex-1 ${((cardType === 'CARD_BOX' && selectedBoxIsOfficial) || (cardType === 'SINGLE_CARD' && selectedGradeInfo?.lowestPriceOnSaleOfficial > 0 && (!selectedGradeInfo?.lowestPriceOnSale || selectedGradeInfo?.lowestPriceOnSaleOfficial <= selectedGradeInfo?.lowestPriceOnSale))) ? 'bg-[#FF5601] hover:bg-[#e64d00] active:bg-[#cc4400]' : ''}`}
            onClick={() => handleBuy((cardType === 'CARD_BOX' && selectedBoxIsOfficial) || (cardType === 'SINGLE_CARD' && selectedGradeInfo?.lowestPriceOnSaleOfficial > 0 && (!selectedGradeInfo?.lowestPriceOnSale || selectedGradeInfo?.lowestPriceOnSaleOfficial <= selectedGradeInfo?.lowestPriceOnSale)))}
            disabled={allOptionsSoldOut}
          >
            {allOptionsSoldOut ? (
              <span className="font-bold">{t('listing.waiting_for_listing')}</span>
            ) : (
              <div className="flex items-center justify-center gap-2">
                <div className="relative">
                  <span className="text-xs">¥</span>
                  <span className="text-lg font-medium ml-[2px]">{chosenPrice.replace('¥', '')}</span>
                </div>
                {((cardType === 'CARD_BOX' && selectedBoxIsOfficial) || (cardType === 'SINGLE_CARD' && selectedGradeInfo?.lowestPriceOnSaleOfficial > 0 && (!selectedGradeInfo?.lowestPriceOnSale || selectedGradeInfo?.lowestPriceOnSaleOfficial <= selectedGradeInfo?.lowestPriceOnSale))) && (
                  <div className="flex items-center gap-[2px]">
                    <img src={officialIcon} alt="Official" className="block w-[10px] h-[10px]" />
                    <div className="font-bold text-[10px] leading-[10px] translate-y-[0.5px] text-white">
                      {t('box.official')}
                    </div>
                  </div>
                )}
              </div>
            )}
          </Button>
        )}
      </div>

      {/* Benefits */}
      <Benefits />

      {/* Brand Info */}
      <div 
        className="mt-4 h-[72px] bg-white border-t border-b border-[#eeeeee] flex items-center justify-between pr-4 cursor-pointer"
        onClick={handleBrandClick}
      >
        <div className="flex items-center gap-4">
          <img src={brandInfo?.logo?.url} alt={brandInfo?.localizedName} className="h-[26px] object-contain" />
          <span className="text-sm">{brandInfo?.localizedName}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-[#aaaaaa]">{t('brand.view_brand')}</span>
          <img src={rightIcon} className="w-5 h-5" alt="View" />
        </div>
      </div>

      {/* Box Cards Info: only render if boxCards has data */}
      {Array.isArray(boxCards) && boxCards.length > 0 && boxCards.flat().length > 0 && (
      <div>
          {/* Title row - move down for vertical centering */}
          <div className="h-[40px] flex items-center bg-white pt-[12px]">
            <span className="text-[14px] font-medium leading-none">{t('listing.related_boxes', 'Related Boxes')}</span>
          </div>
          {/* Card list - height 80px, bottom border */}
          <div className="flex flex-col gap-0 mt-0 pr-4">
            {boxCards.flat().map((item, idx) => (
              <div
                key={item.id || idx}
                className="flex items-center h-[80px] bg-white cursor-pointer border-b border-[#ececec]"
                onClick={() => item.id && navigate(`/card/${item.id}`)}
              >
                {/* Left image section */}
                <div className="w-[56px] h-[56px] rounded bg-white flex items-center justify-center overflow-hidden">
                  <img src={getProductThumbnailUrl(item.images?.[0]?.url)} alt={item.localizedName} className="max-w-[44px] max-h-[44px] object-contain" />
                </div>
                {/* Middle name section */}
                <div className="flex-1 px-4">
                  <span className="text-[12px] text-[#111111] font-normal line-clamp-2 break-all">
                    {item.localizedRawName || item.name}
                    {item.rarityInfo?.sub?.name && `(${item.rarityInfo.sub.name})`}
                  </span>
                </div>
                {/* Right arrow section */}
                <div className="flex items-center justify-center">
                  <img src={rightIcon} alt="chevron right" className="w-5 h-5" />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Favorite Dialog */}
      {isFavoriteDialogOpen && (
        <FavoriteDialog
          onOpenChange={setIsFavoriteDialogOpen}
          selections={favoriteSelections}
          onConfirm={handleFavoriteConfirm}
          onRemoveAll={handleRemoveAllFavorites}
          initialSelectedIds={favoritedItems}
          title={cardType === 'SINGLE_CARD' ? t('favorite.title') : t('favorite.title_box')}
        />
      )}
      
      {/* PSA Inventory Modal */}
      <PSAInventoryModal
        isOpen={psaInventoryModal.isOpen}
        onClose={handleCloseInventoryModal}
        productId={id}
        psa={psaInventoryModal.psa}
        psaLabel={psaInventoryModal.psaLabel}
        inventory={psaInventoryModal.inventory}
        onPurchase={onListingPurchase}
      />
    </div>
  );
};

export default CardBuyInfoSection; 
